import type { Inbox, SenderHealth, ShortLinkDomainState, Suppression } from '../../api/types';

/**
 * What Campaigns → Settings says, kept pure so each sentence is tested rather
 * than eyeballed — above all the ones that must never round "couldn't check"
 * up to a pass.
 */

export type SettingsSection = 'senders' | 'health' | 'links' | 'optout' | 'sms';

export const SETTINGS_SECTIONS: readonly { id: SettingsSection; title: string; sub: string }[] = [
  { id: 'senders', title: 'Senders', sub: 'Mailboxes and numbers' },
  { id: 'health', title: 'Sender health', sub: 'SPF, DKIM, DMARC, mailbox age' },
  { id: 'links', title: 'Link domain', sub: 'Your own address for links' },
  { id: 'optout', title: 'Opting out', sub: 'Footer wording, the opt-out list' },
  { id: 'sms', title: 'Automated texts', sub: 'Default SMS sender' },
];

/**
 * The inboxes a campaign can leave from, by channel — the builder's own rule:
 * every mailbox, and the SMS numbers (a Twilio WhatsApp sender is not one; a
 * bulk send there needs approved templates the builder cannot compose).
 */
export function campaignSenders(inboxes: Inbox[]): { email: Inbox[]; sms: Inbox[] } {
  return {
    email: inboxes.filter((inbox) => inbox.channelType === 'email'),
    sms: inboxes.filter(
      (inbox) =>
        (inbox.channelType === 'twilio' && inbox.channel?.medium !== 'whatsapp') ||
        inbox.channelType === 'ringcentral',
    ),
  };
}

export type CheckState = 'ok' | 'warn' | 'risk' | 'unknown' | 'na';

/** The overall answer, for a chip. `null` health is its own state, never a pass. */
export function healthChip(health: SenderHealth | null | undefined): { state: CheckState; label: string } {
  if (!health) return { state: 'unknown', label: 'Not checked yet' };
  switch (health.level) {
    case 'ok':
      return { state: 'ok', label: 'Looks good' };
    case 'warn':
      return { state: 'warn', label: 'Worth a look' };
    case 'risk':
      return { state: 'risk', label: 'At risk' };
    default:
      return { state: 'unknown', label: 'Couldn’t finish checking' };
  }
}

export interface RecordCheck {
  id: 'spf' | 'dkim' | 'dmarc' | 'age';
  label: string;
  state: CheckState;
  value: string;
}

const has = (health: SenderHealth, id: string) => health.domainAuth.findings.some((f) => f.id === id);
const unanswered = (health: SenderHealth, record: string) =>
  (health.domainAuth.unanswered ?? []).includes(record);
const sentenceCase = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * One tile per thing a receiver checks: SPF, DKIM, DMARC, and how old the
 * mailbox is. Read from the record objects when the service sends them and
 * from the findings when it does not (a payload stored before they existed),
 * and in either case a lookup that did not answer is `unknown`, never `ok`.
 *
 * DKIM is the one that can be `na`: it is asked only where the selector is
 * knowable (Google, Microsoft). A generic mail host signs under a name nobody
 * can guess, so "missing" there would send an operator to fix what may be fine.
 */
export function recordChecks(
  health: SenderHealth,
  provider: 'google' | 'microsoft' | null | undefined,
): RecordCheck[] {
  const auth = health.domainAuth;
  const age: RecordCheck = health.mailboxAge.checked
    ? {
        id: 'age',
        label: 'Mailbox age',
        state: (health.mailboxAge.minAgeDays ?? 0) < 30 ? 'warn' : 'ok',
        value: sentenceCase(health.mailboxAge.label ?? 'unknown'),
      }
    : { id: 'age', label: 'Mailbox age', state: 'unknown', value: 'Couldn’t read the mailbox' };

  // A personal-mail domain's records belong to the provider: whatever DNS says
  // about gmail.com is not this company's, and bulk mail claiming it fails.
  if (auth.freeMail) {
    return [
      { id: 'spf', label: 'SPF', state: 'na', value: 'The provider’s, not yours' },
      { id: 'dkim', label: 'DKIM', state: 'na', value: 'The provider’s, not yours' },
      { id: 'dmarc', label: 'DMARC', state: 'risk', value: 'Fails for bulk mail from this address' },
      age,
    ];
  }
  if (!auth.checked) {
    const value = 'DNS didn’t answer';
    return [
      { id: 'spf', label: 'SPF', state: 'unknown', value },
      { id: 'dkim', label: 'DKIM', state: 'unknown', value },
      { id: 'dmarc', label: 'DMARC', state: 'unknown', value },
      age,
    ];
  }

  let spf: RecordCheck;
  if (has(health, 'spf_missing')) spf = { id: 'spf', label: 'SPF', state: 'risk', value: 'Missing' };
  else if (has(health, 'spf_provider_missing')) {
    spf = { id: 'spf', label: 'SPF', state: 'risk', value: 'Doesn’t cover this mail provider' };
  } else if (auth.spf ? !auth.spf.checked : unanswered(health, 'SPF')) {
    spf = { id: 'spf', label: 'SPF', state: 'unknown', value: 'DNS didn’t answer' };
  } else if (auth.spf && !auth.spf.published) {
    spf = { id: 'spf', label: 'SPF', state: 'risk', value: 'Missing' };
  } else spf = { id: 'spf', label: 'SPF', state: 'ok', value: 'Published' };

  let dkim: RecordCheck;
  if (has(health, 'dkim_missing')) dkim = { id: 'dkim', label: 'DKIM', state: 'risk', value: 'Missing' };
  else if (auth.dkim?.checked) {
    dkim = auth.dkim.published
      ? { id: 'dkim', label: 'DKIM', state: 'ok', value: 'Published' }
      : { id: 'dkim', label: 'DKIM', state: 'risk', value: 'Missing' };
  } else if (provider === 'google' || provider === 'microsoft') {
    dkim = { id: 'dkim', label: 'DKIM', state: 'unknown', value: 'DNS didn’t answer' };
  } else {
    dkim = { id: 'dkim', label: 'DKIM', state: 'na', value: 'Can’t be checked for this mail host' };
  }

  let dmarc: RecordCheck;
  const policy = auth.dmarc?.policy ?? null;
  if (has(health, 'dmarc_missing') || (auth.dmarc?.checked && !auth.dmarc.published)) {
    dmarc = { id: 'dmarc', label: 'DMARC', state: 'risk', value: 'Missing' };
  } else if (has(health, 'dmarc_malformed')) {
    dmarc = { id: 'dmarc', label: 'DMARC', state: 'warn', value: 'Published with no policy' };
  } else if (auth.dmarc ? !auth.dmarc.checked : unanswered(health, 'DMARC')) {
    dmarc = { id: 'dmarc', label: 'DMARC', state: 'unknown', value: 'DNS didn’t answer' };
  } else if (policy === 'none' || has(health, 'dmarc_monitor_only')) {
    dmarc = { id: 'dmarc', label: 'DMARC', state: 'warn', value: 'Monitor only (p=none)' };
  } else {
    dmarc = { id: 'dmarc', label: 'DMARC', state: 'ok', value: policy ? `Published (p=${policy})` : 'Published' };
  }

  return [spf, dkim, dmarc, age];
}

/** What the daily cap is today, in words. */
export function capLine(limit: number | null | undefined): string {
  if (limit === undefined) return 'Leave it empty for the provider’s own allowance.';
  if (limit === null) return 'Now: the provider’s own allowance.';
  if (limit === 0) return 'Now: 0 — this mailbox sends no campaigns or replies.';
  return `Now: ${limit.toLocaleString()} a day.`;
}

/**
 * Two-part public suffixes common among this product's tenants. Not the whole
 * public suffix list — a miss only means the Name column shows the full host,
 * which every DNS provider also accepts with a trailing dot.
 */
const TWO_PART_SUFFIXES = new Set([
  'co.uk', 'org.uk', 'ac.uk', 'gov.uk', 'me.uk', 'ltd.uk', 'plc.uk',
  'com.au', 'net.au', 'org.au',
  'co.nz', 'org.nz',
  'co.za', 'co.in', 'co.jp', 'co.kr', 'co.il',
  'com.br', 'com.mx', 'com.ar', 'com.co', 'com.sg', 'com.hk', 'com.tr', 'com.cn',
]);

/**
 * The record's Name as a DNS provider asks for it: the part in front of the
 * domain being managed — `go` for `go.limosphere.co`. A host that IS a
 * registrable domain (no subdomain) gets `@`, which is what providers call the
 * apex; that one usually cannot take a CNAME at all, which the screen says.
 */
export function recordName(host: string): { name: string; zone: string; apex: boolean } {
  const labels = host.toLowerCase().replace(/\.$/, '').split('.').filter(Boolean);
  const lastTwo = labels.slice(-2).join('.');
  const zoneSize = labels.length > 2 && TWO_PART_SUFFIXES.has(lastTwo) ? 3 : 2;
  if (labels.length <= zoneSize) return { name: '@', zone: labels.join('.'), apex: true };
  return {
    name: labels.slice(0, labels.length - zoneSize).join('.'),
    zone: labels.slice(-zoneSize).join('.'),
    apex: false,
  };
}

export type StepState = 'done' | 'current' | 'upcoming';

/**
 * Where the three set-up steps stand: pick a subdomain, add the record, check
 * it. Until a domain is saved only the first is open; once saved and not yet
 * pointing here, the record is the step to do and the check follows it.
 */
export function linkDomainSteps(state: ShortLinkDomainState): [StepState, StepState, StepState] {
  const domain = state.domain;
  if (!domain) return ['current', 'upcoming', 'upcoming'];
  if (domain.verified) return ['done', 'done', 'done'];
  return ['done', 'current', 'upcoming'];
}

/**
 * The check's answer as a banner, read "Title — detail". Verified stays
 * verified through a lookup that did not ANSWER (the service keeps it in use,
 * and every link sent on it working) — so that case says "still in use",
 * never "not in use".
 */
export function linkDomainStatus(
  state: ShortLinkDomainState,
): { tone: 'ok' | 'warn' | 'info'; title: string; detail: string } | null {
  const domain = state.domain;
  if (!domain) return null;
  if (domain.verified) {
    return domain.error
      ? {
          tone: 'warn',
          title: 'Still in use',
          detail: 'the last check got no answer from DNS, so links keep using it. Check again shortly.',
        }
      : { tone: 'ok', title: 'In use', detail: `campaign links go out on ${domain.host}.` };
  }
  if (!domain.error) {
    return {
      tone: 'info',
      title: 'Not checked yet',
      detail: `press Check again once the record for ${domain.host} is added.`,
    };
  }
  // The server's sentence ends by naming the record to add — the step above
  // already shows it, so only the finding itself is repeated here.
  const first = domain.error.split(' — ')[0].replace(/\.$/, '');
  // Mid-sentence after the dash: "The DNS lookup…" reads as "the DNS lookup…";
  // a host name or an acronym is left as written.
  const finding = /^[A-Z][a-z]/.test(first) ? first.charAt(0).toLowerCase() + first.slice(1) : first;
  return {
    tone: 'warn',
    title: 'Not in use yet',
    detail: `${finding}. DNS changes can take up to an hour to be seen.`,
  };
}

export const REASON_LABELS: Record<Suppression['reason'], string> = {
  stop_keyword: 'Texted STOP',
  provider: 'Carrier opt-out',
  unsubscribe_link: 'Pressed Unsubscribe',
  manual: 'Added by hand',
};

/** Who can take a row off, said before anyone presses anything. */
export function releaseHint(row: Suppression): string | null {
  if (row.releasable) return null;
  switch (row.reason) {
    case 'stop_keyword':
    case 'provider':
      return 'Only they can undo this, by texting START.';
    case 'unsubscribe_link':
      return 'Only they can undo this — ask them to opt back in.';
    default:
      return 'Not yours to undo.';
  }
}
